import { useState, useEffect, useCallback } from 'react';
import { getAuth, fullLogout, verifyPin, hasPinSet, verifyBiometric, hasBiometricRegistered, isBiometricAvailable } from '../utils/auth';

interface Props {
  onUnlock: () => void;
}

export default function LockScreen({ onUnlock }: Props) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [checking, setChecking] = useState(false);
  const auth = getAuth();

  useEffect(() => {
    // Check biometric availability and auto-prompt
    async function init() {
      const available = await isBiometricAvailable();
      const registered = hasBiometricRegistered();
      setBiometricAvailable(available && registered);

      // Auto-prompt biometric on mount if available
      if (available && registered) {
        tryBiometric();
      }
    }
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tryBiometric = useCallback(async () => {
    setChecking(true);
    setError('');
    try {
      const ok = await verifyBiometric();
      if (ok) {
        onUnlock();
      } else {
        setError('Biometric verification failed');
      }
    } catch {
      setError('Biometric not available');
    } finally {
      setChecking(false);
    }
  }, [onUnlock]);

  async function tryPin() {
    if (pin.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }
    setChecking(true);
    setError('');
    const ok = await verifyPin(pin);
    setChecking(false);
    if (ok) {
      setPin('');
      onUnlock();
    } else {
      setError('Incorrect PIN');
      setPin('');
    }
  }

  function signOut() {
    fullLogout();
  }

  const hasPin = hasPinSet();

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'var(--bg, #0a0a0a)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{ textAlign: 'center', maxWidth: 320, width: '100%' }}>
        {/* User info */}
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'var(--surface-2)', margin: '0 auto 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24, fontWeight: 700, color: 'var(--text-muted)',
        }}>
          {(auth?.user?.name || auth?.user?.email || '?')[0].toUpperCase()}
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
          {auth?.user?.name || auth?.user?.email || 'Locked'}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 32 }}>
          Session locked
        </div>

        {/* Biometric button */}
        {biometricAvailable && (
          <button
            onClick={tryBiometric}
            disabled={checking}
            style={{
              width: '100%', padding: '14px 20px', borderRadius: 'var(--radius)',
              background: 'var(--accent)', color: '#fff', border: 'none',
              fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 16,
              opacity: checking ? 0.7 : 1,
            }}
          >
            {checking ? 'Verifying...' : 'Unlock with Biometric'}
          </button>
        )}

        {/* PIN input */}
        {hasPin && (
          <div style={{ marginBottom: 16 }}>
            {biometricAvailable && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>or enter your PIN</div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Enter PIN"
                value={pin}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '');
                  if (v.length <= 6) setPin(v);
                }}
                onKeyDown={e => e.key === 'Enter' && tryPin()}
                autoFocus={!biometricAvailable}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)', background: 'var(--surface)',
                  color: 'var(--text)', fontSize: 18, textAlign: 'center',
                  letterSpacing: 8,
                }}
              />
              <button
                onClick={tryPin}
                disabled={checking || pin.length < 4}
                style={{
                  padding: '12px 20px', borderRadius: 'var(--radius)',
                  background: 'var(--surface-2)', color: 'var(--text)',
                  border: '1px solid var(--border)', fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', opacity: pin.length < 4 ? 0.5 : 1,
                }}
              >
                Go
              </button>
            </div>
          </div>
        )}

        {/* No unlock method configured */}
        {!hasPin && !biometricAvailable && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            No PIN or biometric configured. Please sign out and log in again, then set up a PIN in Settings.
          </div>
        )}

        {error && (
          <div style={{ fontSize: 13, color: 'var(--danger, #e74c3c)', marginBottom: 12 }}>
            {error}
          </div>
        )}

        <button
          onClick={signOut}
          style={{
            background: 'none', border: 'none', color: 'var(--text-muted)',
            fontSize: 13, cursor: 'pointer', padding: '8px 16px', marginTop: 8,
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
